import { SeededRng } from "./rng.js";
import { SpatialHash } from "./spatial-hash.js";
import type {
  BossDefinition,
  ContentPack,
  DamageType,
  DomainEvent,
  EnemyDefinition,
  EnemySnapshot,
  GameCommand,
  GameSimulation,
  GameSnapshot,
  JsonValue,
  ReactionDefinition,
  RoundDefinition,
  RunEndReason,
  RunPhase,
  SimulationCheckpoint,
  SimulationOptions,
  SimulationStatistics,
  StatusInstance,
  UiProjection,
  Vec2,
  WeaponDefinition,
  WeaponKind,
  ZoneSnapshot,
} from "./types.js";

const TICKS_PER_SECOND = 30;
const FIELD_CENTER: Vec2 = { x: 500, y: 700 };
const CORE_RADIUS = 70;
const SPAWN_RADIUS_X = 485;
const SPAWN_RADIUS_Y = 685;
const NORMAL_TRANSITION_TICKS = 36;
const BOSS_WARNING_TICKS = 90;
const EPSILON = 0.000_001;

interface MutableEnemy {
  readonly id: string;
  readonly definitionId: string;
  position: { x: number; y: number };
  health: number;
  readonly maxHealth: number;
  readonly speedPerTick: number;
  readonly radius: number;
  readonly impactDamage: number;
  readonly chargeReward: number;
  readonly affinityId: string;
  readonly displacementResistance: number;
  readonly behavior: EnemyDefinition["behavior"];
  readonly splitInto?: { readonly enemyId: string; readonly count: number };
  readonly isBoss: boolean;
  readonly eliteModifiers: readonly string[];
  readonly regenerationPerTick: number;
  mitigation: number;
  statuses: StatusInstance[];
}

interface MutableZone {
  readonly id: string;
  readonly kind: ZoneSnapshot["kind"];
  readonly position: { x: number; y: number };
  readonly radius: number;
  readonly damage: number;
  readonly damageType: DamageType;
  readonly statusId?: string;
  readonly statusStacks: number;
  readonly statusDurationMultiplier: number;
  readonly displacement: number;
  readonly pulseIntervalTicks: number;
  nextPulseTick: number;
  readonly expiresAtTick: number;
}

interface InternalStatistics {
  enemiesDefeated: number;
  bossesDefeated: number;
  damageBySource: Record<string, number>;
  reactionsTriggered: Record<string, number>;
  highestRound: number;
}

interface InternalState {
  tick: number;
  phase: RunPhase;
  phaseBeforePause: RunPhase;
  paused: boolean;
  speed: 1 | 2;
  round: number;
  quota: number;
  spawned: number;
  resolved: number;
  spawnCountdown: number;
  transitionCountdown: number;
  charge: number;
  condensate: number;
  coreIntegrity: number;
  maxIntegrity: number;
  shield: number;
  maxShield: number;
  shieldRechargeBlockedUntil: number;
  nextRepulsionTick: number;
  rngState: number;
  entitySequence: number;
  eventSequence: number;
  enemies: MutableEnemy[];
  zones: MutableZone[];
  weaponNextFire: Partial<Record<WeaponKind, number>>;
  weaponAttackCounts: Partial<Record<WeaponKind, number>>;
  weaponSlowUntil: number;
  runUpgradeLevels: Record<string, number>;
  milestoneOffers: string[];
  pendingPermanentChoices: string[];
  completedMilestones: number[];
  permanentAccess: string[];
  permanentOwned: string[];
  ownedWeapons: WeaponKind[];
  triggeredBossAbilities: string[];
  bossEntityId: string | null;
  bossDefinitionId: string | null;
  bossRemainingTicks: number;
  bossTotalTicks: number;
  passiveChargeRemainder: number;
  endReason: RunEndReason | null;
  statistics: InternalStatistics;
}

function initialState(options: SimulationOptions): InternalState {
  const progression = options.permanentProgression;
  const owned = [...(progression?.permanentOwned ?? [])];
  const ownedWeapons: WeaponKind[] = ["lightning"];
  for (const kind of ["rain", "wind", "hail"] as const) {
    if (owned.includes(`weapon.${kind}.base`)) ownedWeapons.push(kind);
  }
  let maxIntegrity = 20;
  let maxShield = 0;
  if (owned.includes("defense.integrity-reinforcement")) {
    maxIntegrity +=
      options.content.defenses["defense.integrity-reinforcement"]?.parameters
        .amount ?? 0;
  }
  if (owned.includes("defense.weather-shield")) {
    maxShield =
      options.content.defenses["defense.weather-shield"]?.parameters.capacity ??
      0;
  }
  return {
    tick: 0,
    phase: "idle",
    phaseBeforePause: "idle",
    paused: false,
    speed: 1,
    round: 0,
    quota: 0,
    spawned: 0,
    resolved: 0,
    spawnCountdown: 0,
    transitionCountdown: 0,
    charge: 0,
    condensate: progression?.condensate ?? 0,
    coreIntegrity: maxIntegrity,
    maxIntegrity,
    shield: maxShield,
    maxShield,
    shieldRechargeBlockedUntil: 0,
    nextRepulsionTick: 0,
    rngState: options.seed ?? 0x5a17c9e3,
    entitySequence: 0,
    eventSequence: 0,
    enemies: [],
    zones: [],
    weaponNextFire: {},
    weaponAttackCounts: {},
    weaponSlowUntil: 0,
    runUpgradeLevels: {},
    milestoneOffers: [],
    pendingPermanentChoices: [],
    completedMilestones: [...(progression?.completedMilestones ?? [])],
    permanentAccess: [...(progression?.permanentAccess ?? [])],
    permanentOwned: owned,
    ownedWeapons,
    triggeredBossAbilities: [],
    bossEntityId: null,
    bossDefinitionId: null,
    bossRemainingTicks: 0,
    bossTotalTicks: 0,
    passiveChargeRemainder: 0,
    endReason: null,
    statistics: {
      enemiesDefeated: 0,
      bossesDefeated: 0,
      damageBySource: {},
      reactionsTriggered: {},
      highestRound: 0,
    },
  };
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function distanceSquared(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function normalize(x: number, y: number): Vec2 {
  const length = Math.sqrt(x * x + y * y);
  if (length <= EPSILON) return { x: 0, y: 0 };
  return { x: x / length, y: y / length };
}

function isUnknownRecord(
  value: unknown,
): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

class Simulation implements GameSimulation {
  private readonly content: ContentPack;
  private state: InternalState;
  private rng: SeededRng;
  private readonly commands: GameCommand[] = [];
  private readonly events: DomainEvent[] = [];
  private readonly spatialHash = new SpatialHash<MutableEnemy>(100);

  public constructor(options: SimulationOptions) {
    this.content = options.content;
    if (options.checkpoint !== undefined) {
      if (
        options.checkpoint.simulationVersion !==
          options.content.simulationVersion ||
        options.checkpoint.contentVersion !== options.content.contentVersion
      ) {
        throw new Error(
          "Checkpoint is incompatible with the selected simulation/content version.",
        );
      }
      this.state = cloneJson(
        options.checkpoint.state,
      ) as unknown as InternalState;
    } else {
      this.state = initialState(options);
    }
    this.rng = new SeededRng(this.state.rngState);
  }

  public enqueue(command: GameCommand): void {
    this.commands.push(cloneJson(command));
  }

  public advanceTicks(count: number): void {
    if (!Number.isSafeInteger(count) || count < 0)
      throw new RangeError("Tick count must be a non-negative integer.");
    this.applyCommands();
    const speedAtStart = this.state.speed;
    const targetTicks = count * speedAtStart;
    for (let index = 0; index < targetTicks; index += 1) this.step();
  }

  public getSnapshot(): Readonly<GameSnapshot> {
    const boss =
      this.state.bossEntityId === null || this.state.bossDefinitionId === null
        ? null
        : ({
            entityId: this.state.bossEntityId,
            definitionId: this.state.bossDefinitionId,
            phase: this.bossPhase(),
            remainingTicks: this.state.bossRemainingTicks,
            totalTicks: this.state.bossTotalTicks,
          } as const);
    return {
      tick: this.state.tick,
      simulationTimeMs: this.state.tick * (1000 / TICKS_PER_SECOND),
      phase: this.state.phase,
      paused: this.state.paused,
      speed: this.state.speed,
      round: this.state.round,
      quota: this.state.quota,
      spawned: this.state.spawned,
      resolved: this.state.resolved,
      charge: this.state.charge,
      condensate: this.state.condensate,
      core: {
        integrity: this.state.coreIntegrity,
        maxIntegrity: this.state.maxIntegrity,
        shield: this.state.shield,
        maxShield: this.state.maxShield,
        position: FIELD_CENTER,
        radius: CORE_RADIUS,
      },
      enemies: this.state.enemies.map((enemy): EnemySnapshot => ({
        id: enemy.id,
        definitionId: enemy.definitionId,
        position: { ...enemy.position },
        health: enemy.health,
        maxHealth: enemy.maxHealth,
        radius: enemy.radius,
        impactDamage: enemy.impactDamage,
        affinityId: enemy.affinityId,
        isBoss: enemy.isBoss,
        eliteModifiers: [...enemy.eliteModifiers],
        statuses: enemy.statuses.map((status) => ({ ...status })),
      })),
      zones: this.state.zones.map((zone) => ({
        id: zone.id,
        kind: zone.kind,
        position: { ...zone.position },
        radius: zone.radius,
        expiresAtTick: zone.expiresAtTick,
      })),
      boss,
      milestoneOffers: [...this.state.milestoneOffers],
      pendingPermanentChoices: [...this.state.pendingPermanentChoices],
      ownedWeapons: [...this.state.ownedWeapons],
      runUpgradeLevels: { ...this.state.runUpgradeLevels },
      permanentAccess: [...this.state.permanentAccess],
      permanentOwned: [...this.state.permanentOwned],
      endReason: this.state.endReason,
    };
  }

  public getUiProjection(): Readonly<UiProjection> {
    return {
      tick: this.state.tick,
      phase: this.state.phase,
      paused: this.state.paused,
      speed: this.state.speed,
      round: this.state.round,
      quotaRemaining: Math.max(0, this.state.quota - this.state.resolved),
      integrity: this.state.coreIntegrity,
      maxIntegrity: this.state.maxIntegrity,
      shield: this.state.shield,
      charge: this.state.charge,
      condensate: this.state.condensate,
      bossSecondsRemaining:
        this.state.phase === "boss"
          ? this.state.bossRemainingTicks / TICKS_PER_SECOND
          : null,
      milestoneOffers: [...this.state.milestoneOffers],
      pendingPermanentChoices: [...this.state.pendingPermanentChoices],
      canResume: this.state.paused,
      endReason: this.state.endReason,
    };
  }

  public drainEvents(): readonly DomainEvent[] {
    return this.events.splice(0, this.events.length);
  }

  public createCheckpoint(): SimulationCheckpoint {
    this.state.rngState = this.rng.state;
    return {
      checkpointVersion: 1,
      simulationVersion: this.content.simulationVersion,
      contentVersion: this.content.contentVersion,
      state: cloneJson(this.state) as unknown as JsonValue,
    };
  }

  public getStatistics(): SimulationStatistics {
    return cloneJson(this.state.statistics);
  }

  private step(): void {
    this.applyCommands();
    if (
      this.state.paused ||
      this.state.phase === "idle" ||
      this.state.phase === "ended" ||
      this.state.phase === "milestone"
    )
      return;
    this.state.tick += 1;
    this.state.rngState = this.rng.state;
    this.expireStatuses();
    this.regenerateEnemies();
    this.regenerateDefense();
    this.generatePassiveCharge();
    if (this.state.phase === "round") this.tickRound();
    else if (this.state.phase === "boss-warning") this.tickBossWarning();
    else if (this.state.phase === "boss") this.tickBoss();
    if (this.state.phase === "round" || this.state.phase === "boss") {
      this.tickZones();
      this.tickWeapons();
      this.tickMovement();
      this.tickBreach();
      this.tickRepulsion();
    } else if (this.state.phase === "transition") this.tickTransition();
  }

  private applyCommands(): void {
    const pending = this.commands.splice(0, this.commands.length);
    for (const command of pending) {
      switch (command.type) {
        case "start-run":
          if (this.state.phase === "idle" || this.state.phase === "ended")
            this.startRun(command.seed);
          break;
        case "pause-run":
          if (
            !this.state.paused &&
            this.state.phase !== "idle" &&
            this.state.phase !== "ended"
          ) {
            this.state.paused = true;
            this.state.phaseBeforePause = this.state.phase;
            this.emit("run:paused", {});
          }
          break;
        case "resume-run":
          if (this.state.paused) {
            this.state.paused = false;
            this.emit("run:resumed", {});
          }
          break;
        case "set-game-speed":
          this.state.speed = command.speed;
          this.emit("run:speed-changed", { speed: command.speed });
          break;
        case "purchase-upgrade":
          this.purchaseUpgrade(command.upgradeId, true);
          break;
        case "choose-milestone-upgrade":
          this.chooseMilestone(command.upgradeId);
          break;
        case "choose-permanent-unlock":
          this.choosePermanentUnlock(command.id);
          break;
        case "purchase-permanent":
          this.purchasePermanent(command.id);
          break;
        case "abandon-run":
          if (this.state.phase !== "idle" && this.state.phase !== "ended")
            this.endRun("abandoned");
          break;
      }
    }
  }

  private startRun(seed?: number): void {
    const progression = {
      condensate: this.state.condensate,
      completedMilestones: [...this.state.completedMilestones],
      permanentAccess: [...this.state.permanentAccess],
      permanentOwned: [...this.state.permanentOwned],
    };
    this.state = initialState({
      content: this.content,
      seed: seed ?? this.rng.state,
      permanentProgression: progression,
    });
    this.rng = new SeededRng(this.state.rngState);
    this.startRound(1);
    this.emit("run:started", { seed: this.state.rngState });
  }

  private startRound(round: number): void {
    const definition = this.roundDefinition(round);
    this.state.phase = "round";
    this.state.round = round;
    this.state.quota = definition.quota;
    this.state.spawned = 0;
    this.state.resolved = 0;
    this.state.spawnCountdown = 1;
    this.state.statistics.highestRound = Math.max(
      this.state.statistics.highestRound,
      round,
    );
    this.emit("round:started", {
      round,
      quota: definition.quota,
      spawnPatternId: definition.spawnPatternId,
    });
  }

  private tickRound(): void {
    const definition = this.roundDefinition(this.state.round);
    this.state.spawnCountdown -= 1;
    if (
      this.state.spawned < this.state.quota &&
      this.state.spawnCountdown <= 0
    ) {
      const enemyId = this.rng.weighted(
        Object.entries(definition.enemyWeights),
      );
      this.spawnEnemy(enemyId, false, definition.spawnPatternId);
      this.state.spawned += 1;
      this.state.spawnCountdown = definition.spawnIntervalTicks;
    }
    if (
      this.state.spawned >= this.state.quota &&
      this.state.resolved >= this.state.quota &&
      this.nonBossEnemies().length === 0
    ) {
      this.completeRound(definition);
    }
  }

  private completeRound(definition: RoundDefinition): void {
    this.emit("round:completed", { round: this.state.round });
    if (definition.bossId !== undefined || this.state.round % 10 === 0) {
      this.state.phase = "boss-warning";
      this.state.transitionCountdown = BOSS_WARNING_TICKS;
      this.state.bossDefinitionId =
        definition.bossId ?? this.bossForRound(this.state.round).id;
      this.emit("boss:warning", {
        bossId: this.state.bossDefinitionId,
        ticks: BOSS_WARNING_TICKS,
      });
      return;
    }
    if (this.state.round % 10 === 5) {
      this.state.milestoneOffers = this.generateUpgradeOffer();
      this.state.phase = "milestone";
      this.emit("upgrade:offered", { upgrades: this.state.milestoneOffers });
      return;
    }
    this.beginTransition();
  }

  private beginTransition(): void {
    this.state.phase = "transition";
    this.state.transitionCountdown = NORMAL_TRANSITION_TICKS;
  }

  private tickTransition(): void {
    this.state.transitionCountdown -= 1;
    if (this.state.transitionCountdown <= 0)
      this.startRound(this.state.round + 1);
  }

  private tickBossWarning(): void {
    this.state.transitionCountdown -= 1;
    if (this.state.transitionCountdown > 0) return;
    const definition = this.bossForRound(this.state.round);
    const bossId = this.spawnBoss(definition);
    this.state.bossEntityId = bossId;
    this.state.bossDefinitionId = definition.id;
    this.state.bossRemainingTicks = definition.durationTicks;
    this.state.bossTotalTicks = definition.durationTicks;
    this.state.triggeredBossAbilities = ["phase:1"];
    this.state.phase = "boss";
    this.emit("boss:started", {
      bossId: definition.id,
      entityId: bossId,
      durationTicks: definition.durationTicks,
    });
    this.emit("boss:phase-changed", {
      phase: 1,
      remainingTicks: definition.durationTicks,
    });
  }

  private tickBoss(): void {
    const definition = this.bossForRound(this.state.round);
    this.state.bossRemainingTicks -= 1;
    for (const [index, ability] of definition.abilities.entries()) {
      const key = `${String(index)}:${String(ability.triggerRemainingTicks)}`;
      if (
        this.state.bossRemainingTicks <= ability.triggerRemainingTicks &&
        !this.state.triggeredBossAbilities.includes(key)
      ) {
        this.state.triggeredBossAbilities.push(key);
        this.executeBossAbility(
          definition,
          key,
          ability.kind,
          ability.parameters,
        );
      }
    }
    this.updateBossPhase();
    if (this.state.bossRemainingTicks <= 0 && this.state.phase === "boss") {
      const boss = this.state.enemies.find(
        (enemy) => enemy.id === this.state.bossEntityId,
      );
      if (boss !== undefined) {
        const direction = normalize(
          boss.position.x - FIELD_CENTER.x,
          boss.position.y - FIELD_CENTER.y,
        );
        boss.position.x =
          FIELD_CENTER.x + direction.x * (CORE_RADIUS + boss.radius);
        boss.position.y =
          FIELD_CENTER.y + direction.y * (CORE_RADIUS + boss.radius);
        this.emit("boss:arrived", {
          bossId: definition.id,
          x: boss.position.x,
          y: boss.position.y,
        });
      }
      this.endRun("boss-deadline");
    }
  }

  private executeBossAbility(
    definition: BossDefinition,
    key: string,
    kind: BossDefinition["abilities"][number]["kind"],
    parameters: Readonly<Record<string, JsonValue>>,
  ): void {
    if (kind === "summon") {
      const groups = parameters.groups;
      if (Array.isArray(groups)) {
        for (const group of groups as readonly unknown[]) {
          if (!isUnknownRecord(group)) continue;
          const enemyId = group.enemyId;
          const count = group.count;
          if (typeof enemyId === "string" && typeof count === "number") {
            for (let index = 0; index < count; index += 1)
              this.spawnEnemy(enemyId, false, "spawn.scattered-trickle");
          }
        }
      }
    } else if (kind === "mitigation") {
      const value = parameters.value;
      const boss = this.state.enemies.find(
        (enemy) => enemy.id === this.state.bossEntityId,
      );
      if (boss !== undefined && typeof value === "number")
        boss.mitigation = Math.max(0, Math.min(0.95, value));
    } else {
      const durationTicks = parameters.durationTicks;
      if (typeof durationTicks === "number")
        this.state.weaponSlowUntil = Math.max(
          this.state.weaponSlowUntil,
          this.state.tick + durationTicks,
        );
    }
    this.emit("boss:ability-triggered", {
      bossId: definition.id,
      ability: kind,
      key,
    });
  }

  private updateBossPhase(): void {
    const phase = this.bossPhase();
    const phaseKey = `phase:${String(phase)}`;
    const previousPhaseEvent = this.state.triggeredBossAbilities.find(
      (value) => value === phaseKey,
    );
    if (previousPhaseEvent === undefined) {
      this.state.triggeredBossAbilities.push(phaseKey);
      this.emit("boss:phase-changed", {
        phase,
        remainingTicks: this.state.bossRemainingTicks,
      });
    }
  }

  private bossPhase(): 1 | 2 | 3 {
    if (this.state.bossRemainingTicks > 40 * TICKS_PER_SECOND) return 1;
    if (this.state.bossRemainingTicks > 20 * TICKS_PER_SECOND) return 2;
    return 3;
  }

  private tickWeapons(): void {
    for (const kind of this.state.ownedWeapons) {
      const weapon = this.weaponForKind(kind);
      const nextFire = this.state.weaponNextFire[kind] ?? this.state.tick;
      if (this.state.tick < nextFire) continue;
      this.fireWeapon(weapon);
      const interval = Math.max(
        3,
        Math.round(
          this.weaponStat(
            weapon,
            "intervalTicks",
            weapon.baseStats.intervalTicks,
          ),
        ),
      );
      const slow = this.state.tick < this.state.weaponSlowUntil ? 1.5 : 1;
      this.state.weaponNextFire[kind] =
        this.state.tick + Math.round(interval * slow);
    }
  }

  private fireWeapon(weapon: WeaponDefinition): void {
    if (this.state.enemies.length === 0) return;
    let damage = this.weaponStat(weapon, "damage", weapon.baseStats.damage);
    let radius = this.weaponStat(weapon, "radius", weapon.baseStats.radius);
    if (weapon.kind === "rain" && this.featureLevel("needle-rain") > 0)
      damage *= 2;
    if (weapon.kind === "rain" && this.featureLevel("downpour") > 0)
      radius *= 1.35;
    if (weapon.kind === "wind" && this.featureLevel("wind-shear") > 0)
      damage *= 2;
    if (weapon.kind === "hail" && this.featureLevel("glacial-impact") > 0)
      radius *= 1.5;
    const target = this.selectTarget(weapon.targetingProfile);
    if (target === undefined) return;
    const count = (this.state.weaponAttackCounts[weapon.kind] ?? 0) + 1;
    this.state.weaponAttackCounts[weapon.kind] = count;
    let modifiedDamage = damage;
    if (
      this.featureLevel("overcharge") > 0 &&
      weapon.kind === "lightning" &&
      count % 5 === 0
    )
      modifiedDamage *= 2;
    const activeFeatures = Object.entries(this.state.runUpgradeLevels)
      .filter(([, level]) => level > 0)
      .flatMap(
        ([upgradeId]) =>
          this.content.upgrades[upgradeId]?.effects
            .filter((effect) => effect.operation === "unlock")
            .map((effect) => effect.value) ?? [],
      )
      .filter((value): value is string => typeof value === "string");
    this.emit("weapon:fired", {
      weaponId: weapon.id,
      targetId: target.id,
      damage: modifiedDamage,
      radius,
      activeFeatures,
    });
    if (weapon.behavior === "pulse-single-target") {
      this.dealWeaponDamage(target, weapon, modifiedDamage);
      if (this.featureLevel("chain") > 0)
        this.chainFrom(target, weapon, modifiedDamage);
      if (this.featureLevel("static-field") > 0)
        this.areaDamage(
          target.position,
          radius || 90,
          modifiedDamage * 0.2,
          weapon.damageType,
          weapon.id,
          target.id,
        );
    } else if (weapon.behavior === "cluster-zone") {
      this.createZone(
        "rain",
        target.position,
        weapon,
        modifiedDamage,
        radius || 110,
      );
    } else if (weapon.behavior === "gust-area") {
      if (this.featureLevel("cyclone") > 0)
        this.createZone(
          "cyclone",
          target.position,
          weapon,
          modifiedDamage * 0.35,
          radius || 130,
        );
      if (this.featureLevel("compression-front") > 0)
        this.compressArea(target.position, radius || 130, 28);
      this.areaDamage(
        target.position,
        radius || 130,
        modifiedDamage,
        weapon.damageType,
        weapon.id,
      );
      this.displaceArea(
        target.position,
        radius || 130,
        this.weaponStat(weapon, "displacement", weapon.baseStats.displacement),
      );
      if (this.featureLevel("pressure-drop") > 0) {
        this.spatialHash.rebuild(this.state.enemies);
        for (const enemy of this.spatialHash.queryRadius(
          target.position,
          radius || 130,
        ))
          this.applyStatus(enemy, "status.pressure-drop", 1, weapon.id);
      }
    } else {
      const impacts = this.featureLevel("hail-barrage") > 0 ? 3 : 1;
      for (let index = 0; index < impacts; index += 1) {
        const impactTarget =
          index === 0 ? target : this.selectTarget("highest-health");
        if (impactTarget === undefined) break;
        const impactDamage =
          impacts === 1 ? modifiedDamage : modifiedDamage * 0.45;
        const statusStacks = this.weaponStat(
          weapon,
          "statusStacks",
          weapon.baseStats.statusStacks,
        );
        if (weapon.baseStats.statusId !== undefined)
          this.applyStatus(
            impactTarget,
            weapon.baseStats.statusId,
            statusStacks,
            weapon.id,
          );
        if (
          this.featureLevel("deep-freeze") > 0 &&
          this.statusStacks(impactTarget, "status.chill") >= 3
        )
          this.applyStatus(impactTarget, "status.freeze", 1, weapon.id);
        const shatter =
          this.featureLevel("shatter") > 0 &&
          (this.statusStacks(impactTarget, "status.chill") > 0 ||
            this.statusStacks(impactTarget, "status.freeze") > 0);
        const finalImpact = shatter ? impactDamage * 1.6 : impactDamage;
        this.areaDamage(
          impactTarget.position,
          radius || 70,
          finalImpact * 0.35,
          weapon.damageType,
          weapon.id,
          impactTarget.id,
        );
        this.dealWeaponDamage(impactTarget, weapon, finalImpact);
      }
    }
  }

  private dealWeaponDamage(
    target: MutableEnemy,
    weapon: WeaponDefinition,
    damage: number,
  ): void {
    let critical = false;
    const criticalChance = this.weaponStat(
      weapon,
      "criticalChance",
      weapon.baseStats.criticalChance,
    );
    if (this.rng.next() < criticalChance) {
      critical = true;
      damage *= this.weaponStat(
        weapon,
        "criticalMultiplier",
        weapon.baseStats.criticalMultiplier,
      );
    }
    if (weapon.baseStats.statusId !== undefined) {
      const stacks = this.weaponStat(
        weapon,
        "statusStacks",
        weapon.baseStats.statusStacks,
      );
      const durationMultiplier = this.weaponStat(weapon, "statusDuration", 1);
      this.applyStatus(
        target,
        weapon.baseStats.statusId,
        stacks,
        weapon.id,
        durationMultiplier,
      );
    }
    if (
      weapon.kind === "lightning" &&
      this.featureLevel("execution") > 0 &&
      this.normalizedCoreDistance(target) < 0.3
    )
      damage *= 1.5;
    this.dealDamage(target, damage, weapon.damageType, weapon.id, critical);
  }

  private chainFrom(
    first: MutableEnemy,
    weapon: WeaponDefinition,
    damage: number,
  ): void {
    const chainCount = 2 + this.featureLevel("additional-arc");
    const wet = first.statuses.some(
      (status) => status.statusId === "status.wet",
    );
    const range = (wet ? 190 : 135) + this.featureLevel("chain") * 15;
    const hit = new Set([first.id]);
    let previous = first;
    for (let index = 0; index < chainCount; index += 1) {
      this.spatialHash.rebuild(this.state.enemies);
      const next = this.spatialHash
        .queryRadius(previous.position, range)
        .filter((enemy) => !hit.has(enemy.id))
        .sort(
          (a, b) =>
            distanceSquared(a.position, previous.position) -
            distanceSquared(b.position, previous.position),
        )[0];
      if (next === undefined) break;
      hit.add(next.id);
      this.dealDamage(
        next,
        damage * Math.pow(0.75, index + 1),
        weapon.damageType,
        weapon.id,
        false,
      );
      previous = next;
    }
  }

  private createZone(
    kind: MutableZone["kind"],
    position: Vec2,
    weapon: WeaponDefinition,
    damage: number,
    radius: number,
  ): void {
    let durationTicks = Math.max(
      1,
      Math.round(
        this.weaponStat(
          weapon,
          "durationTicks",
          weapon.baseStats.durationTicks,
        ),
      ),
    );
    if (weapon.kind === "rain" && this.featureLevel("downpour") > 0)
      durationTicks = Math.round(durationTicks * 1.5);
    const zone: MutableZone = {
      id: this.nextEntityId("zone"),
      kind,
      position: { ...position },
      radius,
      damage,
      damageType: weapon.damageType,
      ...(weapon.baseStats.statusId === undefined
        ? {}
        : { statusId: weapon.baseStats.statusId }),
      statusStacks: this.weaponStat(
        weapon,
        "statusStacks",
        weapon.baseStats.statusStacks,
      ),
      statusDurationMultiplier: this.weaponStat(weapon, "statusDuration", 1),
      displacement: weapon.baseStats.displacement,
      pulseIntervalTicks: 15,
      nextPulseTick: this.state.tick,
      expiresAtTick:
        this.state.tick +
        (kind === "cyclone" ? Math.max(durationTicks, 90) : durationTicks),
    };
    this.state.zones.push(zone);
    this.emit("zone:created", {
      id: zone.id,
      kind,
      x: position.x,
      y: position.y,
      radius,
    });
  }

  private tickZones(): void {
    for (const zone of this.state.zones) {
      if (zone.kind === "rain" && this.featureLevel("rolling-front") > 0) {
        const tangent = normalize(
          -(zone.position.y - FIELD_CENTER.y),
          zone.position.x - FIELD_CENTER.x,
        );
        zone.position.x += tangent.x * 1.2;
        zone.position.y += tangent.y * 1.2;
      }
      if (this.state.tick < zone.nextPulseTick) continue;
      zone.nextPulseTick += zone.pulseIntervalTicks;
      this.spatialHash.rebuild(this.state.enemies);
      for (const enemy of this.spatialHash.queryRadius(
        zone.position,
        zone.radius,
      )) {
        if (zone.statusId !== undefined)
          this.applyStatus(
            enemy,
            zone.statusId,
            zone.statusStacks,
            zone.id,
            zone.statusDurationMultiplier,
          );
        const saturation =
          zone.kind === "rain" &&
          this.featureLevel("saturation") > 0 &&
          this.statusStacks(enemy, "status.wet") > 0;
        this.dealDamage(
          enemy,
          zone.damage * (saturation ? 1.5 : 1),
          zone.damageType,
          zone.id,
          false,
        );
      }
      if (zone.displacement > 0)
        this.displaceArea(zone.position, zone.radius, zone.displacement);
    }
    this.state.zones = this.state.zones.filter(
      (zone) => zone.expiresAtTick > this.state.tick,
    );
  }

  private areaDamage(
    position: Vec2,
    radius: number,
    damage: number,
    type: DamageType,
    source: string,
    excludedId?: string,
  ): void {
    this.spatialHash.rebuild(this.state.enemies);
    for (const enemy of this.spatialHash.queryRadius(position, radius)) {
      if (enemy.id !== excludedId)
        this.dealDamage(enemy, damage, type, source, false);
    }
  }

  private dealDamage(
    target: MutableEnemy,
    rawDamage: number,
    damageType: DamageType,
    source: string,
    critical: boolean,
  ): void {
    if (!this.state.enemies.some((enemy) => enemy.id === target.id)) return;
    const affinity = this.content.affinities[target.affinityId];
    const affinityMultiplier =
      affinity?.incomingDamageMultipliers[damageType] ?? 1;
    const pressureMultiplier =
      this.statusStacks(target, "status.pressure-drop") > 0 ? 1.2 : 1;
    const finalDamage = Math.max(
      0,
      rawDamage *
        pressureMultiplier *
        affinityMultiplier *
        (1 - target.mitigation),
    );
    target.health -= finalDamage;
    this.state.statistics.damageBySource[source] =
      (this.state.statistics.damageBySource[source] ?? 0) + finalDamage;
    this.emit("enemy:damaged", {
      enemyId: target.id,
      damage: finalDamage,
      damageType,
      source,
      critical,
      weakness: affinityMultiplier > 1,
      resisted: affinityMultiplier < 1,
    });
    this.triggerReactions(target, damageType, finalDamage);
    if (target.health <= 0) this.defeatEnemy(target, source);
  }

  private triggerReactions(
    target: MutableEnemy,
    damageType: DamageType,
    incomingDamage: number,
  ): void {
    for (const reaction of Object.values(this.content.reactions)) {
      if (
        reaction.incomingDamageType !== damageType ||
        !this.reactionAvailable(reaction)
      )
        continue;
      const requiredStatus =
        reaction.requiredStatusId === undefined
          ? undefined
          : target.statuses.find(
              (status) => status.statusId === reaction.requiredStatusId,
            );
      if (
        reaction.requiredStatusId !== undefined &&
        (requiredStatus?.stacks ?? 0) < (reaction.requiredStatusStacks ?? 1)
      )
        continue;
      const cooldownKey = `reaction-cooldown:${reaction.id}`;
      const cooldown = target.statuses.find(
        (status) => status.statusId === cooldownKey,
      );
      if (
        cooldown !== undefined &&
        (cooldown.expiresAtTick ?? 0) > this.state.tick
      )
        continue;
      target.statuses.push({
        statusId: cooldownKey,
        sourceEntityId: reaction.id,
        stacks: 1,
        magnitude: 1,
        appliedAtTick: this.state.tick,
        expiresAtTick: this.state.tick + reaction.internalCooldownTicks,
      });
      if (reaction.consumption === "one-stack" && requiredStatus !== undefined)
        requiredStatus.stacks -= 1;
      if (reaction.consumption === "all-stacks" && requiredStatus !== undefined)
        requiredStatus.stacks = 0;
      if (reaction.applyStatusId !== undefined)
        this.applyStatus(
          target,
          reaction.applyStatusId,
          reaction.applyStatusStacks ?? 1,
          reaction.id,
        );
      const saturationMultiplier =
        reaction.id === "reaction.thunderstorm" &&
        this.featureLevel("saturation") > 0
          ? 1.35
          : 1;
      const bonusDamage =
        incomingDamage * reaction.bonusDamageMultiplier * saturationMultiplier;
      if (bonusDamage > 0) {
        target.health -= bonusDamage;
        this.state.statistics.damageBySource[reaction.id] =
          (this.state.statistics.damageBySource[reaction.id] ?? 0) +
          bonusDamage;
        if (reaction.splashRadius > 0)
          this.areaDamage(
            target.position,
            reaction.splashRadius,
            bonusDamage * 0.5,
            "reaction",
            reaction.id,
            target.id,
          );
      }
      if (reaction.displacement > 0)
        this.displaceArea(
          target.position,
          reaction.splashRadius || 120,
          reaction.displacement,
        );
      if (reaction.zoneBehavior === "move-rain") {
        for (const zone of this.state.zones) {
          if (zone.kind !== "rain") continue;
          const direction = normalize(
            target.position.x - zone.position.x,
            target.position.y - zone.position.y,
          );
          zone.position.x += direction.x * 45;
          zone.position.y += direction.y * 45;
          Object.assign(zone, { kind: "squall" as const });
        }
      }
      this.state.statistics.reactionsTriggered[reaction.id] =
        (this.state.statistics.reactionsTriggered[reaction.id] ?? 0) + 1;
      this.emit("reaction:triggered", {
        reactionId: reaction.id,
        enemyId: target.id,
        damage: bonusDamage,
      });
    }
  }

  private reactionAvailable(reaction: ReactionDefinition): boolean {
    if (
      reaction.requiresCoupler &&
      !this.state.permanentOwned.includes("permanent.atmospheric-coupler")
    )
      return false;
    return reaction.requiredWeaponKinds.every((kind) =>
      this.state.ownedWeapons.includes(kind),
    );
  }

  private applyStatus(
    target: MutableEnemy,
    statusId: string,
    stacks: number,
    source: string,
    durationMultiplier = 1,
  ): void {
    const definition = this.content.statuses[statusId];
    if (definition === undefined) return;
    const modifier =
      this.content.affinities[target.affinityId]?.statusModifiers[statusId] ??
      1;
    const effectiveStacks = Math.max(
      1,
      Math.round(
        stacks * modifier * (target.isBoss ? definition.bossEffectiveness : 1),
      ),
    );
    const existing = target.statuses.find(
      (status) => status.statusId === statusId,
    );
    if (existing === undefined) {
      target.statuses.push({
        statusId,
        sourceEntityId: source,
        stacks: Math.min(definition.maxStacks, effectiveStacks),
        magnitude: 1,
        appliedAtTick: this.state.tick,
        expiresAtTick:
          this.state.tick +
          Math.round(definition.durationTicks * durationMultiplier),
      });
    } else {
      if (definition.stackPolicy === "add")
        existing.stacks = Math.min(
          definition.maxStacks,
          existing.stacks + effectiveStacks,
        );
      if (definition.stackPolicy === "replace")
        existing.stacks = Math.min(definition.maxStacks, effectiveStacks);
      if (definition.stackPolicy === "max")
        existing.stacks = Math.max(
          existing.stacks,
          Math.min(definition.maxStacks, effectiveStacks),
        );
      if (definition.refreshPolicy === "refresh")
        existing.expiresAtTick =
          this.state.tick +
          Math.round(definition.durationTicks * durationMultiplier);
      if (definition.refreshPolicy === "extend")
        existing.expiresAtTick =
          (existing.expiresAtTick ?? this.state.tick) +
          Math.round(definition.durationTicks * durationMultiplier);
    }
    this.emit("status:applied", {
      enemyId: target.id,
      statusId,
      stacks: effectiveStacks,
    });
  }

  private expireStatuses(): void {
    for (const enemy of this.state.enemies) {
      enemy.statuses = enemy.statuses.filter(
        (status) =>
          status.expiresAtTick === null ||
          status.expiresAtTick > this.state.tick,
      );
    }
  }

  private regenerateEnemies(): void {
    for (const enemy of this.state.enemies) {
      if (enemy.regenerationPerTick > 0)
        enemy.health = Math.min(
          enemy.maxHealth,
          enemy.health + enemy.regenerationPerTick,
        );
    }
  }

  private tickMovement(): void {
    this.spatialHash.rebuild(this.state.enemies);
    const bossDuration = Math.max(1, this.state.bossTotalTicks);
    for (const enemy of this.state.enemies) {
      let speed = enemy.speedPerTick;
      if (enemy.isBoss) {
        const remainingRatio = Math.max(
          0,
          this.state.bossRemainingTicks / bossDuration,
        );
        const angle = Math.atan2(
          enemy.position.y - FIELD_CENTER.y,
          enemy.position.x - FIELD_CENTER.x,
        );
        const breachDistance = CORE_RADIUS + enemy.radius;
        enemy.position.x =
          FIELD_CENTER.x +
          Math.cos(angle) *
            (breachDistance +
              (SPAWN_RADIUS_X - breachDistance) * remainingRatio);
        enemy.position.y =
          FIELD_CENTER.y +
          Math.sin(angle) *
            (breachDistance +
              (SPAWN_RADIUS_Y - breachDistance) * remainingRatio);
        continue;
      }
      for (const status of enemy.statuses) {
        const definition = this.content.statuses[status.statusId];
        if (definition !== undefined)
          speed *= Math.pow(
            definition.movementMultiplierPerStack,
            status.stacks,
          );
      }
      const towardCore = normalize(
        FIELD_CENTER.x - enemy.position.x,
        FIELD_CENTER.y - enemy.position.y,
      );
      let directionX = towardCore.x;
      let directionY = towardCore.y;
      const neighbors = this.spatialHash.queryRadius(
        enemy.position,
        Math.max(45, enemy.radius * 3),
      );
      for (const neighbor of neighbors) {
        if (neighbor.id === enemy.id) continue;
        const away = normalize(
          enemy.position.x - neighbor.position.x,
          enemy.position.y - neighbor.position.y,
        );
        directionX += away.x * 0.16;
        directionY += away.y * 0.16;
      }
      if (
        enemy.behavior === "spiral" ||
        this.roundDefinition(this.state.round).spawnPatternId ===
          "spawn.spiral-entry"
      ) {
        directionX += -towardCore.y * 0.22;
        directionY += towardCore.x * 0.22;
      }
      const direction = normalize(directionX, directionY);
      enemy.position.x += direction.x * speed;
      enemy.position.y += direction.y * speed;
    }
  }

  private tickBreach(): void {
    const breached = this.state.enemies
      .filter(
        (enemy) =>
          distanceSquared(enemy.position, FIELD_CENTER) <=
          Math.pow(CORE_RADIUS + enemy.radius, 2),
      )
      .sort(
        (a, b) =>
          this.normalizedCoreDistance(a) - this.normalizedCoreDistance(b) ||
          a.id.localeCompare(b.id),
      );
    for (const enemy of breached) {
      if (!this.state.enemies.some((candidate) => candidate.id === enemy.id))
        continue;
      if (enemy.isBoss) {
        this.endRun("boss-deadline");
        return;
      }
      this.removeEnemy(enemy.id);
      this.state.resolved += 1;
      let remainingImpact = enemy.impactDamage;
      if (this.state.shield > 0) {
        const absorbed = Math.min(this.state.shield, remainingImpact);
        this.state.shield -= absorbed;
        remainingImpact -= absorbed;
      }
      if (remainingImpact > 0)
        this.state.coreIntegrity = Math.max(
          0,
          this.state.coreIntegrity - remainingImpact,
        );
      this.state.shieldRechargeBlockedUntil =
        this.state.tick + 5 * TICKS_PER_SECOND;
      this.emit("enemy:impacted-core", {
        enemyId: enemy.id,
        impactDamage: enemy.impactDamage,
      });
      this.emit("core:damaged", {
        amount: remainingImpact,
        integrity: this.state.coreIntegrity,
        shield: this.state.shield,
      });
      if (this.state.coreIntegrity <= 0) {
        this.endRun("core-destroyed");
        return;
      }
    }
  }

  private tickRepulsion(): void {
    if (
      !this.state.permanentOwned.includes("defense.repulsion-system") ||
      this.state.tick < this.state.nextRepulsionTick
    )
      return;
    const definition = this.content.defenses["defense.repulsion-system"];
    const radius = definition?.parameters.radius ?? 210;
    const displacement = definition?.parameters.displacement ?? 55;
    const cooldownTicks =
      definition?.parameters.cooldownTicks ?? 8 * TICKS_PER_SECOND;
    const nearCore = this.state.enemies.some(
      (enemy) =>
        !enemy.isBoss &&
        distanceSquared(enemy.position, FIELD_CENTER) <= radius * radius,
    );
    if (!nearCore) return;
    this.displaceArea(FIELD_CENTER, radius, displacement);
    this.state.nextRepulsionTick = this.state.tick + cooldownTicks;
    this.emit("defense:repulsion-pulse", {});
  }

  private regenerateDefense(): void {
    if (
      this.state.maxShield <= 0 ||
      this.state.tick < this.state.shieldRechargeBlockedUntil ||
      this.state.shield >= this.state.maxShield
    )
      return;
    const rechargePerSecond =
      this.content.defenses["defense.weather-shield"]?.parameters
        .rechargePerSecond ?? 0.04;
    this.state.shield = Math.min(
      this.state.maxShield,
      this.state.shield + rechargePerSecond / TICKS_PER_SECOND,
    );
  }

  private generatePassiveCharge(): void {
    this.state.passiveChargeRemainder +=
      this.content.economy.passiveChargePerSecond / TICKS_PER_SECOND;
    const whole = Math.floor(this.state.passiveChargeRemainder);
    if (whole > 0) {
      this.state.charge += whole;
      this.state.passiveChargeRemainder -= whole;
    }
  }

  private defeatEnemy(enemy: MutableEnemy, source: string): void {
    if (!this.state.enemies.some((candidate) => candidate.id === enemy.id))
      return;
    this.removeEnemy(enemy.id);
    this.state.charge += enemy.chargeReward;
    this.state.statistics.enemiesDefeated += enemy.isBoss ? 0 : 1;
    this.emit("enemy:defeated", {
      enemyId: enemy.id,
      definitionId: enemy.definitionId,
      source,
      reward: enemy.chargeReward,
    });
    if (enemy.isBoss) {
      this.completeBoss();
      return;
    }
    this.state.resolved += 1;
    if (enemy.splitInto !== undefined) {
      for (let index = 0; index < enemy.splitInto.count; index += 1) {
        this.spawnEnemyAt(enemy.splitInto.enemyId, enemy.position, false);
        this.state.quota += 1;
      }
    }
  }

  private completeBoss(): void {
    const definition = this.bossForRound(this.state.round);
    this.state.statistics.bossesDefeated += 1;
    this.state.charge += definition.rewardCharge;
    this.state.condensate +=
      definition.rewardCondensate + this.content.economy.condensatePerBoss;
    this.state.bossEntityId = null;
    this.emit("boss:defeated", {
      bossId: definition.id,
      round: this.state.round,
    });
    const firstClear = !this.state.completedMilestones.includes(
      this.state.round,
    );
    if (firstClear) {
      this.state.completedMilestones.push(this.state.round);
      this.state.condensate +=
        this.content.economy.firstClearBonus[this.state.round] ?? 0;
      this.preparePermanentChoice(this.state.round);
    }
    if (this.state.pendingPermanentChoices.length > 0) {
      this.state.phase = "milestone";
      this.emit("permanent:choice-offered", {
        choices: this.state.pendingPermanentChoices,
      });
    } else {
      this.beginTransition();
    }
  }

  private preparePermanentChoice(round: number): void {
    const weather = [
      "weapon.rain.base",
      "weapon.wind.base",
      "weapon.hail.base",
    ].filter((id) => !this.state.permanentAccess.includes(id));
    const defenses = [
      "defense.weather-shield",
      "defense.integrity-reinforcement",
      "defense.repulsion-system",
    ].filter((id) => !this.state.permanentAccess.includes(id));
    if (round === 10 || round === 20)
      this.state.pendingPermanentChoices = weather;
    if (round === 30 && weather[0] !== undefined) this.grantAccess(weather[0]);
    if (round === 40 || round === 50)
      this.state.pendingPermanentChoices = defenses;
    if (round === 60 && defenses[0] !== undefined)
      this.grantAccess(defenses[0]);
    if (round >= 40) this.grantAccess("permanent.atmospheric-coupler");
  }

  private choosePermanentUnlock(id: string): void {
    if (
      this.state.phase !== "milestone" ||
      !this.state.pendingPermanentChoices.includes(id)
    )
      return;
    this.grantAccess(id);
    this.state.pendingPermanentChoices = [];
    this.emit("permanent:access-granted", { id });
    this.beginTransition();
  }

  private grantAccess(id: string): void {
    if (!this.state.permanentAccess.includes(id))
      this.state.permanentAccess.push(id);
  }

  private purchasePermanent(id: string): void {
    if (this.state.phase !== "idle" && this.state.phase !== "ended") return;
    if (
      !this.state.permanentAccess.includes(id) ||
      this.state.permanentOwned.includes(id)
    )
      return;
    const defense = this.content.defenses[id];
    const cost = id.startsWith("weapon.")
      ? this.content.economy.firstWeatherCost
      : (defense?.cost ?? this.content.economy.firstWeatherCost);
    if (this.state.condensate < cost) return;
    this.state.condensate -= cost;
    this.state.permanentOwned.push(id);
    for (const kind of ["rain", "wind", "hail"] as const) {
      if (
        id === `weapon.${kind}.base` &&
        !this.state.ownedWeapons.includes(kind)
      )
        this.state.ownedWeapons.push(kind);
    }
    if (defense?.kind === "integrity") {
      const amount = defense.parameters.amount ?? 0;
      this.state.maxIntegrity += amount;
      this.state.coreIntegrity += amount;
    }
    if (defense?.kind === "shield") {
      this.state.maxShield = defense.parameters.capacity ?? 0;
      this.state.shield = this.state.maxShield;
    }
    this.emit("permanent:purchased", { id, cost });
  }

  private chooseMilestone(upgradeId: string): void {
    if (
      this.state.phase !== "milestone" ||
      !this.state.milestoneOffers.includes(upgradeId)
    )
      return;
    if (!this.purchaseUpgrade(upgradeId, false)) return;
    this.state.milestoneOffers = [];
    this.emit("upgrade:chosen", { upgradeId });
    this.beginTransition();
  }

  private purchaseUpgrade(upgradeId: string, costsCharge: boolean): boolean {
    const upgrade = this.content.upgrades[upgradeId];
    if (upgrade === undefined) return false;
    if (costsCharge && upgrade.category !== "basic") return false;
    if (!costsCharge && upgrade.category !== "major") return false;
    const level = this.state.runUpgradeLevels[upgradeId] ?? 0;
    if (
      level >= upgrade.maxLevel ||
      !this.requirementsMet(upgrade.prerequisites) ||
      this.requirementsMetAny(upgrade.exclusions)
    )
      return false;
    const cost = Math.floor(
      upgrade.baseCost * Math.pow(upgrade.costGrowth, level),
    );
    if (costsCharge && this.state.charge < cost) return false;
    if (costsCharge) this.state.charge -= cost;
    this.state.runUpgradeLevels[upgradeId] = level + 1;
    this.emit("upgrade:purchased", {
      upgradeId,
      level: level + 1,
      cost: costsCharge ? cost : 0,
    });
    return true;
  }

  private requirementsMet(
    requirements: readonly {
      readonly kind: string;
      readonly id?: string;
      readonly value?: number;
    }[],
  ): boolean {
    return requirements.every((requirement) => {
      if (requirement.kind === "upgrade")
        return (this.state.runUpgradeLevels[requirement.id ?? ""] ?? 0) > 0;
      if (requirement.kind === "weapon-owned")
        return this.state.ownedWeapons.includes(requirement.id as WeaponKind);
      if (requirement.kind === "permanent-owned")
        return this.state.permanentOwned.includes(requirement.id ?? "");
      if (requirement.kind === "round-at-least")
        return (
          this.state.round >= (requirement.value ?? Number.POSITIVE_INFINITY)
        );
      return false;
    });
  }

  private requirementsMetAny(
    requirements: readonly {
      readonly kind: string;
      readonly id?: string;
      readonly value?: number;
    }[],
  ): boolean {
    return (
      requirements.length > 0 &&
      requirements.some((requirement) => this.requirementsMet([requirement]))
    );
  }

  private generateUpgradeOffer(): string[] {
    const candidates = Object.values(this.content.upgrades).filter(
      (upgrade) => {
        const level = this.state.runUpgradeLevels[upgrade.id] ?? 0;
        return (
          upgrade.category === "major" &&
          level < upgrade.maxLevel &&
          this.requirementsMet(upgrade.prerequisites) &&
          !this.requirementsMetAny(upgrade.exclusions) &&
          (upgrade.weaponKind === undefined ||
            this.state.ownedWeapons.includes(upgrade.weaponKind)) &&
          (this.state.round > 15 || upgrade.complexity <= 2)
        );
      },
    );
    const guaranteed = candidates.filter(
      (upgrade) => upgrade.guaranteedOfferRound === this.state.round,
    );
    const offer = guaranteed.slice(0, 1).map((upgrade) => upgrade.id);
    const remaining = candidates.filter(
      (upgrade) => !offer.includes(upgrade.id),
    );
    while (offer.length < 3 && remaining.length > 0) {
      const selectedId = this.rng.weighted(
        remaining.map((upgrade) => [upgrade.id, upgrade.offerWeight] as const),
      );
      offer.push(selectedId);
      const index = remaining.findIndex((upgrade) => upgrade.id === selectedId);
      remaining.splice(index, 1);
    }
    return offer;
  }

  private weaponStat(
    weapon: WeaponDefinition,
    stat: string,
    baseValue: number,
  ): number {
    let value = baseValue;
    for (const [upgradeId, level] of Object.entries(
      this.state.runUpgradeLevels,
    )) {
      const upgrade = this.content.upgrades[upgradeId];
      if (
        upgrade?.weaponKind !== undefined &&
        upgrade.weaponKind !== weapon.kind
      )
        continue;
      for (const effect of upgrade?.effects ?? []) {
        if (effect.stat !== stat || typeof effect.value !== "number") continue;
        for (let index = 0; index < level; index += 1) {
          if (effect.operation === "add") value += effect.value;
          if (effect.operation === "multiply") value *= effect.value;
        }
      }
    }
    return value;
  }

  private featureLevel(feature: string): number {
    let level = 0;
    for (const [upgradeId, upgradeLevel] of Object.entries(
      this.state.runUpgradeLevels,
    )) {
      const upgrade = this.content.upgrades[upgradeId];
      if (
        upgrade?.effects.some(
          (effect) => effect.operation === "unlock" && effect.value === feature,
        ) === true
      )
        level += upgradeLevel;
    }
    return level;
  }

  private selectTarget(profile: string): MutableEnemy | undefined {
    const enemies = [...this.state.enemies];
    if (profile === "highest-health")
      return enemies.sort(
        (a, b) => b.health - a.health || a.id.localeCompare(b.id),
      )[0];
    if (profile === "lowest-health")
      return enemies.sort(
        (a, b) => a.health - b.health || a.id.localeCompare(b.id),
      )[0];
    if (profile === "highest-impact")
      return enemies.sort(
        (a, b) => b.impactDamage - a.impactDamage || a.id.localeCompare(b.id),
      )[0];
    if (profile === "densest-cluster") {
      this.spatialHash.rebuild(enemies);
      return enemies.sort(
        (a, b) =>
          this.spatialHash.queryRadius(b.position, 140).length -
            this.spatialHash.queryRadius(a.position, 140).length ||
          a.id.localeCompare(b.id),
      )[0];
    }
    if (profile === "elemental-priority") {
      return enemies.sort(
        (a, b) =>
          Number(b.affinityId === "affinity.fire") -
            Number(a.affinityId === "affinity.fire") || b.health - a.health,
      )[0];
    }
    if (profile === "random-valid")
      return enemies[this.rng.integer(0, enemies.length)];
    return enemies.sort(
      (a, b) =>
        this.normalizedCoreDistance(a) - this.normalizedCoreDistance(b) ||
        b.impactDamage - a.impactDamage ||
        a.id.localeCompare(b.id),
    )[0];
  }

  private displaceArea(
    position: Vec2,
    radius: number,
    magnitude: number,
  ): void {
    this.spatialHash.rebuild(this.state.enemies);
    for (const enemy of this.spatialHash.queryRadius(position, radius)) {
      if (enemy.isBoss) continue;
      const direction = normalize(
        enemy.position.x - FIELD_CENTER.x,
        enemy.position.y - FIELD_CENTER.y,
      );
      const displacement = magnitude * (1 - enemy.displacementResistance);
      enemy.position.x += direction.x * displacement;
      enemy.position.y += direction.y * displacement;
    }
  }

  private compressArea(
    position: Vec2,
    radius: number,
    magnitude: number,
  ): void {
    this.spatialHash.rebuild(this.state.enemies);
    for (const enemy of this.spatialHash.queryRadius(position, radius)) {
      if (enemy.isBoss) continue;
      const direction = normalize(
        position.x - enemy.position.x,
        position.y - enemy.position.y,
      );
      const displacement = magnitude * (1 - enemy.displacementResistance);
      enemy.position.x += direction.x * displacement;
      enemy.position.y += direction.y * displacement;
    }
  }

  private statusStacks(enemy: MutableEnemy, statusId: string): number {
    return (
      enemy.statuses.find((status) => status.statusId === statusId)?.stacks ?? 0
    );
  }

  private spawnEnemy(
    definitionId: string,
    isBoss: boolean,
    patternId: string,
  ): string {
    const position = this.spawnPosition(patternId, this.state.spawned);
    return this.spawnEnemyAt(definitionId, position, isBoss);
  }

  private spawnEnemyAt(
    definitionId: string,
    position: Vec2,
    isBoss: boolean,
  ): string {
    const definition = this.content.enemies[definitionId];
    if (definition === undefined)
      throw new Error(`Unknown enemy definition: ${definitionId}`);
    const healthScale = 1 + 0.14 * Math.max(0, this.state.round - 1);
    const eliteModifiers = !isBoss ? this.selectEliteModifiers() : [];
    let maxHealth = definition.baseHealth * healthScale;
    let speedMultiplier = 1;
    let mitigation = 0;
    let regenerationPerSecond = 0;
    let displacementResistance = definition.displacementResistance;
    let affinityId = definition.affinityId;
    for (const modifierId of eliteModifiers) {
      const modifier = this.content.eliteModifiers[modifierId];
      if (modifier === undefined)
        throw new Error(`Unknown elite modifier: ${modifierId}`);
      maxHealth *= modifier.healthMultiplier;
      speedMultiplier *= modifier.speedMultiplier;
      mitigation = Math.max(mitigation, modifier.mitigation);
      regenerationPerSecond += modifier.regenerationPerSecond;
      displacementResistance = Math.max(
        displacementResistance,
        modifier.displacementResistance,
      );
      if (modifier.affinityOverride !== undefined)
        affinityId = modifier.affinityOverride;
    }
    const id = this.nextEntityId(isBoss ? "boss" : "enemy");
    const enemy: MutableEnemy = {
      id,
      definitionId,
      position: { ...position },
      health: maxHealth,
      maxHealth,
      speedPerTick:
        (definition.speedPerSecond * speedMultiplier) / TICKS_PER_SECOND,
      radius: definition.radius,
      impactDamage: definition.impactDamage,
      chargeReward: Math.max(
        1,
        Math.round(
          definition.chargeReward *
            (1 + 0.05 * Math.floor((this.state.round - 1) / 5)),
        ),
      ),
      affinityId,
      displacementResistance,
      behavior: definition.behavior,
      ...(definition.splitInto === undefined
        ? {}
        : { splitInto: definition.splitInto }),
      isBoss,
      eliteModifiers,
      regenerationPerTick: regenerationPerSecond / TICKS_PER_SECOND,
      mitigation,
      statuses: [],
    };
    this.state.enemies.push(enemy);
    this.emit("enemy:spawned", {
      enemyId: id,
      definitionId,
      x: position.x,
      y: position.y,
      isBoss,
      eliteModifiers,
    });
    return id;
  }

  private spawnBoss(definition: BossDefinition): string {
    const angle = this.rng.next() * Math.PI * 2;
    const tier = Math.floor(Math.max(0, this.state.round - 10) / 30);
    const id = this.nextEntityId("boss");
    const health =
      definition.baseHealth * Math.pow(definition.healthScalePerTier, tier);
    this.state.enemies.push({
      id,
      definitionId: definition.id,
      position: {
        x: FIELD_CENTER.x + Math.cos(angle) * SPAWN_RADIUS_X,
        y: FIELD_CENTER.y + Math.sin(angle) * SPAWN_RADIUS_Y,
      },
      health,
      maxHealth: health,
      speedPerTick: 0,
      radius: 52,
      impactDamage: definition.impactDamage,
      chargeReward: definition.rewardCharge,
      affinityId: definition.affinityId,
      displacementResistance: 1,
      behavior: "radial",
      isBoss: true,
      eliteModifiers: [],
      regenerationPerTick: 0,
      mitigation: 0,
      statuses: [],
    });
    this.emit("enemy:spawned", {
      enemyId: id,
      definitionId: definition.id,
      isBoss: true,
    });
    return id;
  }

  private spawnPosition(patternId: string, index: number): Vec2 {
    const pattern = this.content.spawnPatterns[patternId];
    let angle = this.rng.next() * Math.PI * 2;
    if (pattern?.behavior === "clustered")
      angle = Math.floor(index / 5) * 1.7 + (this.rng.next() - 0.5) * 0.45;
    if (pattern?.behavior === "ring") angle = (index % 8) * (Math.PI / 4);
    if (pattern?.behavior === "alternating")
      angle = (index % 2) * Math.PI + (this.rng.next() - 0.5) * 0.35;
    if (pattern?.behavior === "spiral") angle = index * 0.62;
    if (pattern?.behavior === "portal") angle = Math.floor(index / 6) * 1.9;
    return {
      x: FIELD_CENTER.x + Math.cos(angle) * SPAWN_RADIUS_X,
      y: FIELD_CENTER.y + Math.sin(angle) * SPAWN_RADIUS_Y,
    };
  }

  private selectEliteModifiers(): string[] {
    if (this.state.round < 20) return [];
    const eligible = Object.values(this.content.eliteModifiers).filter(
      (modifier) => modifier.earliestRound <= this.state.round,
    );
    if (eligible.length === 0) return [];
    const chance = Math.min(0.35, 0.08 + (this.state.round - 20) * 0.005);
    if (this.rng.next() >= chance) return [];
    const first = this.rng.weighted(
      eligible.map((modifier) => [modifier.id, modifier.weight] as const),
    );
    const result = [first];
    if (this.state.round >= 40 && this.rng.next() < 0.25) {
      const remaining = eligible.filter((modifier) => modifier.id !== first);
      if (remaining.length > 0)
        result.push(
          this.rng.weighted(
            remaining.map(
              (modifier) => [modifier.id, modifier.weight] as const,
            ),
          ),
        );
    }
    return result;
  }

  private roundDefinition(round: number): RoundDefinition {
    const exact = this.content.rounds.find(
      (definition) => definition.round === round,
    );
    if (exact !== undefined) return exact;
    const latest = this.content.rounds[this.content.rounds.length - 1];
    if (latest === undefined) throw new Error("Content pack has no rounds.");
    const bossId = round % 10 === 0 ? this.bossForRound(round).id : undefined;
    return {
      round,
      quota: 8 + Math.floor((round - 1) * 2.2),
      spawnIntervalTicks: Math.max(
        3,
        latest.spawnIntervalTicks - Math.floor((round - latest.round) / 10),
      ),
      spawnPatternId: latest.spawnPatternId,
      enemyWeights: latest.enemyWeights,
      ...(bossId === undefined ? {} : { bossId }),
    };
  }

  private bossForRound(round: number): BossDefinition {
    const explicitId =
      this.content.rounds.find((definition) => definition.round === round)
        ?.bossId ?? this.state.bossDefinitionId;
    if (typeof explicitId === "string") {
      const explicit = this.content.bosses[explicitId];
      if (explicit !== undefined) return explicit;
    }
    const bosses = Object.values(this.content.bosses);
    if (bosses.length === 0) throw new Error("Content pack has no bosses.");
    const boss = bosses[(Math.floor(round / 10) - 1) % bosses.length];
    if (boss === undefined)
      throw new Error("Boss rotation failed to resolve a definition.");
    return boss;
  }

  private weaponForKind(kind: WeaponKind): WeaponDefinition {
    const weapon = Object.values(this.content.weapons).find(
      (definition) => definition.kind === kind,
    );
    if (weapon === undefined)
      throw new Error(`Content pack has no ${kind} weapon.`);
    return weapon;
  }

  private nonBossEnemies(): readonly MutableEnemy[] {
    return this.state.enemies.filter((enemy) => !enemy.isBoss);
  }

  private normalizedCoreDistance(enemy: MutableEnemy): number {
    return (
      Math.sqrt(distanceSquared(enemy.position, FIELD_CENTER)) /
      Math.max(SPAWN_RADIUS_X, SPAWN_RADIUS_Y)
    );
  }

  private removeEnemy(id: string): void {
    this.state.enemies = this.state.enemies.filter((enemy) => enemy.id !== id);
  }

  private nextEntityId(prefix: string): string {
    this.state.entitySequence += 1;
    return `${prefix}-${String(this.state.entitySequence)}`;
  }

  private emit(
    type: string,
    payload: Readonly<Record<string, JsonValue>>,
  ): void {
    this.state.eventSequence += 1;
    this.events.push({
      sequence: this.state.eventSequence,
      tick: this.state.tick,
      type,
      payload,
    });
  }

  private endRun(reason: RunEndReason): void {
    if (this.state.phase === "ended") return;
    this.state.phase = "ended";
    this.state.endReason = reason;
    const earned = Math.max(
      0,
      Math.floor(this.state.round * this.content.economy.condensatePerRound),
    );
    this.state.condensate += earned;
    this.emit("run:ended", {
      reason,
      round: this.state.round,
      condensateEarned: earned,
    });
  }
}

export function createGameSimulation(
  options: SimulationOptions,
): GameSimulation {
  return new Simulation(options);
}
